import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  BadgeCheck, Clock, CheckCircle, XCircle, 
  RefreshCw, User, Calendar, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import AdminLayout from "@/components/AdminLayout";

const AdminVerifications = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.getVerificationRequests(filter);
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load verification requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      await api.reviewVerification(requestId, "approve");
      toast.success("Verification approved!");
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to approve verification");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    setProcessing(requestId);
    try {
      await api.reviewVerification(requestId, "reject", rejectionReason);
      toast.success("Verification rejected");
      setSelectedRequest(null);
      setRejectionReason("");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to reject verification");
    } finally {
      setProcessing(null);
    }
  };

  const filters = [
    { id: "pending", label: "Pending", icon: Clock, color: "text-amber-600" },
    { id: "approved", label: "Approved", icon: CheckCircle, color: "text-green-600" },
    { id: "rejected", label: "Rejected", icon: XCircle, color: "text-red-600" },
    { id: "all", label: "All", icon: BadgeCheck, color: "text-gray-600" }
  ];

  return (
    <AdminLayout>
    <div className="bg-gray-50" data-testid="admin-verifications-page">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-[#1C3A7A]">Verification Requests</h1>
              <p className="text-sm text-gray-500">{requests.length} requests</p>
            </div>
            <Button
              onClick={fetchRequests}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === f.id
                    ? 'bg-[#1C3A7A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <f.icon className={`w-4 h-4 ${filter === f.id ? 'text-white' : f.color}`} />
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FAB710] border-t-transparent" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <BadgeCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-1">No {filter} requests</h3>
            <p className="text-sm text-gray-500">
              {filter === "pending" ? "All verification requests have been reviewed" : `No ${filter} verification requests found`}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {requests.map((req) => (
              <div
                key={req.id}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
                data-testid={`verification-request-${req.id}`}
              >
                {/* User Info */}
                <div className="p-4 border-b flex items-center gap-3">
                  <img
                    src={req.user?.photos?.[0] || "/placeholder-user.png"}
                    alt={req.user?.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{req.user?.name || "Unknown"}</p>
                    <p className="text-xs text-gray-500">{req.user?.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    req.status === "pending" ? "bg-amber-100 text-amber-700" :
                    req.status === "approved" ? "bg-green-100 text-green-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {req.status}
                  </span>
                </div>

                {/* Selfie */}
                <div className="aspect-square bg-gray-100 relative">
                  <img
                    src={req.selfie_url}
                    alt="Verification selfie"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => setSelectedRequest(req)}
                    className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center transition-colors group"
                  >
                    <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>Submitted {format(new Date(req.created_at), "MMM d, yyyy")}</span>
                  </div>

                  {req.status === "pending" && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApprove(req.id)}
                        disabled={processing === req.id}
                        className="flex-1 bg-green-500 hover:bg-green-600"
                        size="sm"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setSelectedRequest(req)}
                        disabled={processing === req.id}
                        variant="outline"
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                        size="sm"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}

                  {req.status === "rejected" && req.rejection_reason && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg p-2">
                      Reason: {req.rejection_reason}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-[#1C3A7A] px-4 py-3 flex items-center justify-between">
              <h3 className="text-white font-semibold">Review Verification</h3>
              <button onClick={() => { setSelectedRequest(null); setRejectionReason(""); }} className="text-white/80 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* User Info */}
              <div className="flex items-center gap-4 mb-4 pb-4 border-b">
                <img
                  src={selectedRequest.user?.photos?.[0] || "/placeholder-user.png"}
                  alt={selectedRequest.user?.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">{selectedRequest.user?.name}</p>
                  <p className="text-sm text-gray-500">{selectedRequest.user?.email}</p>
                  <p className="text-xs text-gray-400">
                    Joined {format(new Date(selectedRequest.user?.created_at || Date.now()), "MMM d, yyyy")}
                  </p>
                </div>
              </div>

              {/* Side by side comparison */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Profile Photo</p>
                  <img
                    src={selectedRequest.user?.photos?.[0] || "/placeholder-user.png"}
                    alt="Profile"
                    className="w-full aspect-square object-cover rounded-xl"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Verification Selfie</p>
                  <img
                    src={selectedRequest.selfie_url}
                    alt="Selfie"
                    className="w-full aspect-square object-cover rounded-xl"
                  />
                </div>
              </div>

              {/* Actions */}
              {selectedRequest.status === "pending" && (
                <>
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Rejection reason (required if rejecting)
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="e.g., Photo is blurry, face not clearly visible..."
                      className="w-full p-3 border rounded-lg resize-none h-20"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleApprove(selectedRequest.id)}
                      disabled={processing === selectedRequest.id}
                      className="flex-1 bg-green-500 hover:bg-green-600"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Verification
                    </Button>
                    <Button
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={processing === selectedRequest.id || !rejectionReason.trim()}
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminVerifications;
